import { forwardRef } from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Facebook, Mail, MapPin, Phone } from 'lucide-react';
const logo = '/images/logo-sm.webp';

const TikTokIcon = forwardRef<SVGSVGElement, React.SVGProps<SVGSVGElement>>((props, ref) => (
  <svg ref={ref} viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5" {...props}>
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
));
TikTokIcon.displayName = 'TikTokIcon';

const socialLinks = [
  { icon: Instagram, href: 'https://www.instagram.com/raven__moon__photography?igsh=MTYwdGhnOW5wcnF3Nw==', label: 'Instagram' },
  { icon: Facebook, href: 'https://www.facebook.com/share/188TB5SuQ6/', label: 'Facebook' },
  { icon: TikTokIcon, href: 'https://www.tiktok.com/@raven_moon_photography?_r=1&_t=ZP-92dXV8YF0HP', label: 'TikTok', isCustom: true },
];

export const Footer = forwardRef<HTMLElement, object>((_, ref) => {
  return (
    <footer className="relative bg-card/80 backdrop-blur-sm border-t border-border/30 overflow-hidden" role="contentinfo">
      {/* Subtle atmospheric gradient */}
      <div className="absolute inset-0 pointer-events-none" style={{
        background: 'radial-gradient(ellipse at 50% 0%, hsl(270 30% 15% / 0.08), transparent 60%)'
      }} />

      <div className="container mx-auto px-4 py-16 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="space-y-4">
            <img src={logo} alt="Raven Moon Photography" className="h-20 w-auto" width="80" height="80" />
            <p className="text-muted-foreground text-sm leading-relaxed font-light">
              Capturing life's most precious moments through the lens of artistry and passion.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-cinzel text-xs tracking-[0.2em] uppercase text-primary mb-5">Quick Links</h4>
            <nav className="space-y-3" aria-label="Quick links">
              {[
                { label: 'Portfolio', path: '/portfolio' },
                { label: 'Shop', path: '/shop' },
                { label: 'Fine Art Prints', path: '/prints' },
                { label: 'FAQ', path: '/faq' },
                { label: 'Book Now', path: '/booking' },
              ].map((item) => (
                <Link
                  key={item.label}
                  to={item.path}
                  className="block text-sm text-muted-foreground hover:text-primary transition-all duration-500 font-light"
                >
                  {item.label}
                </Link>
              ))}
            </nav>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-cinzel text-xs tracking-[0.2em] uppercase text-primary mb-5">Services</h4>
            <nav className="space-y-3" aria-label="Services">
              {[
                { label: 'Portrait Photography', path: '/pricing' },
                { label: 'Wedding & Events', path: '/pricing' },
                { label: 'Boudoir Photography', path: '/pricing' },
                { label: 'Reveal Experience', path: '/reveal-experience' },
                { label: 'Fine Art Prints', path: '/prints' },
              ].map((item) => (
                <Link
                  key={item.label}
                  to={item.path}
                  className="block text-sm text-muted-foreground hover:text-primary transition-all duration-500 font-light"
                >
                  {item.label}
                </Link>
              ))}
            </nav>
          </div>

          {/* Contact & Social */}
          <div>
            <h4 className="font-cinzel text-xs tracking-[0.2em] uppercase text-primary mb-5">Get In Touch</h4>
            <div className="space-y-3 text-sm text-muted-foreground font-light">
              <a
                href="mailto:ravenmoonphotography0@gmail.com"
                className="flex items-center gap-2 hover:text-primary transition-all duration-500"
              >
                <Mail className="h-4 w-4 text-primary/60" />
                ravenmoonphotography0@gmail.com
              </a>
              <a
                href="tel:+14805766053"
                className="flex items-center gap-2 hover:text-primary transition-all duration-500"
              >
                <Phone className="h-4 w-4 text-primary/60" />
                (480) 576-6053
              </a>
              <div className="flex items-start gap-2">
                <MapPin className="h-4 w-4 mt-0.5 text-primary/60" />
                <div>
                  <span className="text-foreground/80">Portland, Oregon</span>
                  <p className="text-xs text-muted-foreground mt-0.5">Available for travel worldwide</p>
                </div>
              </div>
              
              {/* Social Links */}
              <div className="pt-5">
                <p className="font-cinzel text-[10px] tracking-[0.2em] uppercase text-muted-foreground mb-3">Follow Along</p>
                <div className="flex gap-5">
                  {socialLinks.map((social) => (
                    <a
                      key={social.label}
                      href={social.href}
                      className="text-foreground/40 hover:text-primary transition-all duration-500"
                      aria-label={social.label}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {'isCustom' in social ? <social.icon /> : <social.icon className="h-5 w-5" />}
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom bar with filigree */}
        <div className="mt-14 pt-8 relative">
          <div className="absolute top-0 left-0 right-0 h-px" style={{
            background: 'linear-gradient(90deg, transparent, hsl(42 65% 52% / 0.2), transparent)'
          }} />
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-muted-foreground font-light tracking-wider">
            <p>© {new Date().getFullYear()} Raven Moon Photography LLC. All rights reserved.</p>
            <div className="flex gap-6">
              <Link to="/privacy" className="hover:text-primary transition-all duration-500">
                Privacy Policy
              </Link>
              <Link to="/terms" className="hover:text-primary transition-all duration-500">
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
});

Footer.displayName = 'Footer';